import React, { PropsWithChildren, ReactNode } from 'react';
import { css } from '@emotion/react';
import { Link, useNavigate } from '@tanstack/react-router';
import { theme } from '@frontend/theme';
import { BreadcrumbPathProps, Breadcrumbs, SpinningLoader, Text } from '@frontend/design-system';
import { PageTitle } from './page-title';

type MaxWidthProp = string | number | null;

export type PageProps = PropsWithChildren<{
  action?: React.ReactNode;
  breadcrumbs?: BreadcrumbPathProps[];
  children?: ReactNode;
  className?: string;
  customBackUrl?: string;
  customBackFn?: () => void;
  icon?: string;
  id?: string;
  loading?: boolean;
  maxWidth?: MaxWidthProp;
  showBackBtn?: boolean;
  subtitle?: string | React.ReactNode;
  title?: React.ReactNode;
}>;

const maxWidthStyle = (val: PageProps['maxWidth']) => {
  if (val === null) {
    return null;
  } else if (typeof val === 'number') {
    return css`
      max-width: ${val}px;
    `;
  } else {
    return css`
      max-width: ${val};
    `;
  }
};

const page = (maxWidth: MaxWidthProp) => {
  return css`
    @keyframes fadeIn {
      0% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }
    min-height: 100%;
    container-type: inline-size;
    opacity: 0;
    animation: fadeIn ease 0.15s;
    animation-fill-mode: forwards;
    display: grid;
    grid-template-areas: 'header' 'content';
    grid-template-rows: auto 1fr;
    grid-template-columns: 100%;
    background-color: ${theme.colors.neutral10};
    width: 100%;
    min-width: 800px;
    font-size: ${theme.font.size.medium};
    padding: ${theme.spacing(4)};
    ${maxWidthStyle(maxWidth) ?? 'max-width: 1000px;'}
  `;
};

const pageHeader = css`
  margin-bottom: ${theme.spacing(4)};
  grid-area: header;
  &:empty {
    display: none;
  }
`;

const pageContent = css`
  grid-area: content;
`;

export const Page = ({
  action,
  breadcrumbs,
  children,
  className,
  customBackUrl,
  customBackFn,
  id,
  loading,
  maxWidth = 1400,
  showBackBtn = false,
  subtitle,
  title,
  ...rest
}: PageProps) => {
  const navigate = useNavigate();
  if (loading) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <SpinningLoader size='xl' />
      </div>
    );
  }

  let onBackClick: () => void = () => history.go(-1);
  if (customBackFn) {
    onBackClick = customBackFn;
  } else if (customBackUrl) {
    onBackClick = () => navigate({ to: customBackUrl });
  }

  const shouldShowHeaderContent = !!title || !!subtitle || !!action;

  return (
    <div css={page(maxWidth)} className={className} id={id} {...rest}>
      <header css={pageHeader} className='page-header'>
        {breadcrumbs?.length && (
          <Breadcrumbs css={{ marginBottom: theme.spacing(1) }}>
            {breadcrumbs.map(({ to, label, ...rest }) => (
              <Breadcrumbs.Path key={label} as={Link} to={to} {...rest}>
                {label}
              </Breadcrumbs.Path>
            ))}
          </Breadcrumbs>
        )}
        {shouldShowHeaderContent && (
          <HeaderContent
            title={title}
            subtitle={subtitle}
            action={action}
            showBackBtn={showBackBtn}
            onBackClick={onBackClick}
          />
        )}
      </header>
      <div css={pageContent}>{children}</div>
    </div>
  );
};

export const HeaderContent = ({
  action,
  showBackBtn = false,
  onBackClick,
  subtitle,
  title,
}: PageProps & { onBackClick?: () => void }) => {
  return (
    <div
      css={{
        display: 'grid',
        gridTemplateAreas: '"title title actions" "subtitle subtitle subtitle"',
        gridTemplateColumns: 'repeat(auto-fit, minmax(0, auto))',
        gridTemplateRows: 'auto auto',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      <div
        css={{
          gridArea: 'title',
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1),
        }}
      >
        {typeof title === 'string' ? (
          <PageTitle title={title} showBackBtn={showBackBtn && history.length > 0} onBackClick={onBackClick} />
        ) : (
          title
        )}
      </div>
      {subtitle && typeof subtitle === 'string' && (
        <Text
          css={{
            gridArea: 'subtitle',
            margin: 0,
          }}
        >
          {subtitle}
        </Text>
      )}
      {subtitle && typeof subtitle !== 'string' && <div style={{ gridArea: 'subtitle', margin: 0 }}>{subtitle}</div>}

      {action && (
        <div
          css={{
            gridArea: 'actions',
            width: 'fit-content',
            justifySelf: 'end',
          }}
          className='page-action'
        >
          {action}
        </div>
      )}
    </div>
  );
};
